import { ref, reactive, watch, computed, isRef, unref, onMounted, onServerPrefetch } from "vue";
import type { MaybeRef } from "vue";
import SearchSpringApi from "@/helpers/api/SearchSpringAPI.ts";
import { type CategorySearch } from "@interfaces/SearchSpringCategorySearch";
import type { SearchTermSearch } from "@/interfaces/SearchSpringTermSearch";
import { watchDebounced, useDebounceFn} from "@vueuse/core";
import { router, usePage } from '@inertiajs/vue3'
import he from "he";
import _ from "lodash";
import { attemptServerPrefetch } from "./attemptServerPrefetch";

export function useCategorySearch({
    searchSpringApi,
    category,
    filters,
    sorts
}: {
    searchSpringApi: SearchSpringApi;
    category: any;
    filters?: Array<any>;
    sorts?: Array<any>;
    merchandising?: any;
}) {
    let searchSpringResults: CategorySearch = reactive({
        facets: [],
        results: [],
        sorting: {
            options: []
        },
        pagination: {
            totalResults: "0",
            totalPages: "0",
            currentPage: "0",
            perPage: "0"
        },
        merchandising: {
            inline: []
        }
    });

    const defaultSort = ref();
    const paginationKey = ref(0);
    const key = ref(0);
    const activeFilters = ref([]);

    const rememberKey = computed(()=>{
        const key = category;
        return key ? `${key}/useCategorySearch/` : null;
    })
    const rememberedState = computed(()=>{
        if(rememberKey.value){
            return new Proxy({
                page: router.restore(rememberKey.value + 'page'),
                filters: router.restore(rememberKey.value + 'filters'),
                sorts: router.restore(rememberKey.value + 'sorts'),
            },{
                get(target, prop){
                    return target[prop]
                },
                set(target,prop,value){
                    router.remember(value, rememberKey.value + prop);
                    target[prop] = value;
                    return true;
                }
            })
        }else{
            return null;
        }
    })

    onMounted(async () => {
        try {
            Object.assign(
                searchSpringResults,
                await searchSpringApi.categorySearch({ 
                    category,
                    filters: rememberedState.value?.filters ? rememberedState.value.filters : filters,
                    sorts: rememberedState.value?.sorts ? [rememberedState.value.sorts] : undefined,
                    page: rememberedState.value?.page ? +rememberedState.value.page : undefined 
                })
            );
            paginationKey.value++;
            key.value++;

            if(rememberedState.value?.filters){
                activeFilters.value = rememberedState.value?.filters;
            }
            if(rememberedState.value?.sorts){
                defaultSort.value = rememberedState.value?.sorts;
            }
        } catch {
            // SearchSpring may be blocked or unavailable — keep empty results
        }
    });

    async function handleSort(sortOption: {
        field: string;
        direction: string;
    }) {
        defaultSort.value = sortOption;
        try {
            Object.assign(
                searchSpringResults,
                await searchSpringApi.categorySearch({
                    category,
                    sorts: [sortOption]
                })
            );
            paginationKey.value++;
            key.value++;
            if(rememberKey.value) rememberedState.value.sorts = JSON.parse(JSON.stringify(defaultSort.value))
        } catch {
            // ignore SearchSpring failures
        }
    }

    async function handleFilter(filters) {
        filters = filters.map((filter) => {
            return {
                field: filter.facet.field,
                value: filter.value.value,
                multiple: filter.facet.multiple,
                low: filter.value?.low,
                high: filter.value?.high
            };
        });

        activeFilters.value = filters;
        sorts = defaultSort.value ? [defaultSort.value] : [];

        try {
            Object.assign(
                searchSpringResults,
                await searchSpringApi.categorySearch({ 
                    category, 
                    filters: activeFilters.value,
                    sorts,
                    page: 1
                })
            );
            paginationKey.value++;
            key.value++;
            if(rememberKey.value){
                rememberedState.value.filters = JSON.parse(JSON.stringify(filters))
                rememberedState.value.page = 1;
            }
        } catch {
            // ignore SearchSpring failures
        }
    }

    async function handlePagination(page: number) {
        const sorts = defaultSort.value ? [defaultSort.value] : [];
        try {
            Object.assign(
                searchSpringResults,
                await searchSpringApi.categorySearch({ category, sorts, page })
            );
            paginationKey.value++;
            key.value++;
            if(rememberKey.value) rememberedState.value.page = page;
        } catch {
            // ignore SearchSpring failures
        }
    }

    return {
        searchSpringResults,
        handleSort,
        handleFilter,
        handlePagination,
        defaultSort,
        paginationKey,
        key
    };
}
export function useSearchByTerm({
    searchSpringApi,
    searchTerm,
    filters,
    sorts,
    tag
}: {
    searchSpringApi: SearchSpringApi;
    searchTerm?: MaybeRef<string>;
    filters?: Array<any>;
    sorts?: Array<any>;
    merchandising?: any;
    tag?: MaybeRef<string>;
}) {
    let searchSpringResults: SearchTermSearch = ref({
        facets: [{ label: "test" }],
        results: [],
        sorting: {
            options: []
        },
        pagination: {
            totalResults: 0,
            totalPages: 1,
            currentPage: 1,
            maxPages: 1,
            perPage: 10
        },
        merchandising: {
            inline: []
        }
    });

    const initialLoad = ref(true);
    const defaultSort = ref();
    const paginationKey = ref(0);
    const key = ref(0);
    const activeFilters = ref([]);

    function searchFilters(nextFilters?: Array<any>) {
        if (!filters?.length) {
            return nextFilters;
        }
        const applied = Array.isArray(nextFilters) ? nextFilters : [];
        const lockedFields = new Set(filters.map((filter) => filter.field));
        return [
            ...filters,
            ...applied.filter((filter) => !lockedFields.has(filter.field))
        ];
    }
    
    const rememberKey = computed(()=>{
        const key = searchTerm || tag || null;
        return key ? `${key}/useSearchByTerm/` : null;
    })
    const rememberedState = computed(()=>{
        if(rememberKey.value){
            return new Proxy({
                page: router.restore(rememberKey.value + 'page'),
                filters: router.restore(rememberKey.value + 'filters'),
                sorts: router.restore(rememberKey.value + 'sorts'),
            },{
                get(target, prop){
                    return target[prop]
                },
                set(target,prop,value){
                    router.remember(value, rememberKey.value + prop);
                    target[prop] = value;
                    return true;
                }
            })
        }else{
            return null;
        }
    })

    onServerPrefetch(async () => {
        let searchData = {};

        if(searchTerm){
            searchData = {
                searchTerm: searchTerm?.value ?? searchTerm ?? undefined,
            };
        }

        if(tag){
            searchData = {
                tag
            };
        }
        searchSpringApi
            .search({
                ...searchData, 
                filters: searchFilters(rememberedState.value?.filters ? rememberedState.value.filters : filters),
                sorts: rememberedState.value?.sorts ? [rememberedState.value.sorts] : undefined,
                page: rememberedState.value?.page ? +rememberedState.value.page : undefined 
            })
            .then((response) => {
                initialLoad.value = false;
                searchSpringResults.value = response;
                key.value++;
            })
            .catch(() => {
                initialLoad.value = false;
            });
        paginationKey.value++;

        if(rememberedState.value?.filters){
            activeFilters.value = rememberedState.value?.filters;
        }
        if(rememberedState.value?.sorts){
            defaultSort.value = rememberedState.value?.sorts;
        }        
    });

    onMounted(async () => {
        let searchData = {};
        if(searchSpringResults.value?.results?.length === 0){
            if(searchTerm){
                searchData = {
                    searchTerm: searchTerm?.value ?? searchTerm ?? undefined,
                };
            }
    
            if(tag){
                searchData = {
                    tag
                };
            }
            searchSpringApi
                .search({
                    ...searchData, 
                    filters: searchFilters(rememberedState.value?.filters ? rememberedState.value.filters : filters),
                    sorts: rememberedState.value?.sorts ? [rememberedState.value.sorts] : undefined,
                    page: rememberedState.value?.page ? +rememberedState.value.page : undefined 
                })
                .then((response) => {
                    initialLoad.value = false;
                    searchSpringResults.value = response;
                    key.value++;
                })
                .catch(() => {
                    initialLoad.value = false;
                });
            paginationKey.value++;
    
            if(rememberedState.value?.filters){
                activeFilters.value = rememberedState.value?.filters;
            }
            if(rememberedState.value?.sorts){
                defaultSort.value = rememberedState.value?.sorts;
            }        
        }

        if(isRef(searchTerm)){
            watchDebounced(searchTerm, async (newVal, oldVal) => {
                if(newVal === oldVal){
                    return;
                }
                try {
                    searchSpringResults.value = await searchSpringApi.search({
                        searchTerm: newVal,
                        filters: searchFilters(activeFilters.value),
                        sorts: defaultSort.value ? [defaultSort.value] : [],
                        page: 1,
                    });
                    key.value++;
                    paginationKey.value++;
                } catch {
                    // ignore SearchSpring failures
                }
            }, { debounce: 250 });
        }
    
    })
    async function handleSort() {
        const sortOption = defaultSort.value;
        try {
            searchSpringResults.value = await searchSpringApi.search({
                searchTerm: searchTerm?.value ?? searchTerm ?? undefined,
                sorts: [sortOption],
                filters: searchFilters(activeFilters.value)
            });
            key.value++;
            paginationKey.value++;
            if(rememberKey.value) rememberedState.value.sorts = JSON.parse(JSON.stringify(defaultSort.value))
        } catch {
            // ignore SearchSpring failures
        }
    }
    async function handlePagination(page: number) {
        const sorts = defaultSort.value ? [defaultSort.value] : [];
        try {
            searchSpringResults.value = await searchSpringApi.search({
                searchTerm: searchTerm?.value ?? searchTerm ?? undefined,
                sorts,
                page,
                filters: searchFilters(activeFilters.value)
            });
            key.value++;
            paginationKey.value++;
            if(rememberKey.value) rememberedState.value.page = page;
        } catch {
            // ignore SearchSpring failures
        }
    }
    async function handleFilter(filters) {
        filters = filters.map((filter) => {
            return {
                field: filter.facet.field,
                value: filter.value.value,
                multiple: filter.facet.multiple,
                low: filter.value?.low,
                high: filter.value?.high
            };
        });

        activeFilters.value = filters;
        sorts = defaultSort.value ? [defaultSort.value] : [];
        try {
            let response = await searchSpringApi.search({
                searchTerm: searchTerm?.value ?? searchTerm ?? undefined,
                filters: searchFilters(activeFilters.value),
                sorts,
                page: 1
            })
            searchSpringResults.value = response;
            key.value++;
            paginationKey.value++;
            if(rememberKey.value){
                rememberedState.value.filters = JSON.parse(JSON.stringify(filters));
                rememberedState.value.page = 1;
            }
        } catch {
            // ignore SearchSpring failures
        }
    }
    const data = computed(()=>{
        return searchSpringResults.value.results;
    });

    return {
        searchSpringResults,
        handleSort,
        handlePagination,
        handleFilter,
        defaultSort,
        key,
        data,
        paginationKey,
        initialLoad
    };
}

export function useTrending({
    searchSpringApi
}: {
    searchSpringApi: SearchSpringApi;
}) {
    const trendingSearches = ref({});
    onMounted(async () => {
        try {
            trendingSearches.value = await searchSpringApi.trendingSearches();
        } catch {
            // ignore SearchSpring failures
        }
    });
    return { trendingSearches };
}

export function useCampaign({
    searchSpringApi,
    tag,
    new_arrivals,
    is_best_sellers,
    filters
}: {
    searchSpringApi: SearchSpringApi;
    tag: Ref<string>;
    new_arrivals: Ref<boolean>;
    is_best_sellers: Ref<boolean>;
    filters?: MaybeRef<Array<any>>;
}) {
    tag = ref(tag);
    const data = ref({
        results: []
    });

    const filtersArray = computed(()=>{
        const resolved = isRef(filters) ? unref(filters) : filters;
        return Array.isArray(resolved) ? resolved : [];
    })

    async function fetchResults() {
        try {
            if(tag.value){
                const response = await searchSpringApi.campaign({
                    tag: tag.value,
                    filters: filtersArray.value
                });
                data.value.results = response ? response.results : [];
            }else if(new_arrivals?.value){
                const response = await searchSpringApi.search({
                    filters: [{
                        field: 'is_new',
                        value: 'true',
                        multiple: 'single'
                    }, ...filtersArray.value]
                })
                data.value.results = response ? response.results : [];
            }else if(is_best_sellers?.value){
                const response = await searchSpringApi.search({
                    filters: [...filtersArray.value]
                })
                data.value.results = response ? response.results : [];
            }
        } catch {
            data.value.results = [];
        }
    }

    onMounted(async () => {
        await fetchResults();

        const handleDebounce = useDebounceFn(async (newVal) => {
            if(!newVal){
                data.value.results = [];
                return;
            }
            try {
                const response = await searchSpringApi.campaign({
                    tag: newVal,
                    filters: filtersArray.value
                });
                data.value.results = response ? response.results : [];
            } catch {
                data.value.results = [];
            }
        }, 1000);

        watch(tag, handleDebounce);

        watch(new_arrivals, async (newVal) => {
            if(newVal){
                try {
                    const response = await searchSpringApi.search({
                        filters: [{
                            field: 'is_new',
                            value: 'true',
                            multiple: 'single'
                        }, ...filtersArray.value]
                    })
                    data.value.results = response ? response.results : [];
                } catch {
                    data.value.results = [];
                }
            }
        });

        watch(is_best_sellers, async (newVal) => {
            if(newVal){
                try {
                    const response = await searchSpringApi.search({
                        filters: [...filtersArray.value]
                    })
                    data.value.results = response ? response.results : [];
                } catch {
                    data.value.results = [];
                }
            }
        });

        watch(filtersArray, async () => {
            await fetchResults();
        });

    });

   


    return { data };
}


export function useContent({
    searchTerm,
    bgFilter,
    filters,
    sorts
} : {
    searchTerm: MaybeRef<string>;
    bgFilter: {
        filter: string;
        value: string;
    };
    filters?: Array<any>;
    sorts?: Array<any>;
}){
    let searchSpringApi = new SearchSpringApi({
        adapter: 'fetch',
        baseUrl: `https://njzl9n.a.searchspring.io`,
        siteId: 'njzl9n'
    });
    
    searchSpringApi.setBgFilter({
        filter: bgFilter.filter,
        value: bgFilter.value
    })
    let response = useSearchByTerm({
        searchSpringApi,
        searchTerm,
        filters,
        sorts
    });
    let data = computed(()=>{
        return response.data.value.map((item)=>{
            return {
                ...item,
                category: he.decode(item.category)?.split(">")?.pop?.() ?? "",
            }
        })
    })
    return {...response, data};
}


export function useBoost({
    product,
    products,
    tags,
    shopperId,
    cart,
    limit = 5,
    filters,
    categories
}: {
    product?: MaybeRef<string>,
    products?: MaybeRef<string[]>,
    tags: MaybeRef<string[]>,
    shopperId?: MaybeRef<string>,
    cart?: MaybeRef<string[]>,
    limit?: MaybeRef<number>,
    filters?: MaybeRef<Array<any>>,
    categories?: MaybeRef<string[]>,
}){
    let searchSpringApi = new SearchSpringApi({
        adapter: 'fetch',
    });

    let payload = computed(()=>{

        let optional = {};
        if(unref(shopperId)){
            optional['shopperId'] = unref(shopperId);
        }

        if(unref(cart)){
            optional['cart'] = unref(cart);
        }

        if(unref(limit)){
            optional['limit'] = unref(limit);
        }else{
            optional['limit'] = 5;
        }

        if(unref(filters)){
            optional['filters'] = unref(filters);
        }

        if(unref(product)){
            return {
                products: [unref(product)],
                tags: unref(tags),
                ...optional
            }
        }

        if(unref(categories)){
            return {
                categories: unref(categories),
                tags: unref(tags),
                ...optional
            }
        }
        return {
            products: unref(products) ?? [],
            tags: unref(tags),
            ...optional
        }
    })
    const response = ref([]);
    onMounted(async ()=>{
        searchSpringApi.boost(payload.value).then(res =>{
            response.value = res ?? [];
        }).catch(() => {
            response.value = [];
        });

    })
    watch(payload, async (newVal, oldVal) => {
        if(_.isEqual(newVal,oldVal)){
            return;
        }
        try {
            response.value = (await searchSpringApi.boost(newVal)) ?? [];
        } catch {
            response.value = [];
        }
    }, {
        deep: true,
    });
    return {response};
}